import { useState } from 'react'

import { VirtualCell } from '../context/virtualGrid'
import { Cell } from '../types'

export type RowSelection = ReadonlyMap<string, string>

export const EMPTY_ROW_SELECTION: RowSelection = new Map()

function buildRowSelection(selectedCells: Cell[], virtualSelectedCells: VirtualCell[]) {
    const rows = new Map<string, Map<string, string>>()
    for (const cell of selectedCells) {
        const row = rows.get(cell.rowId) ?? new Map<string, string>()
        row.set(cell.colId, '')
        rows.set(cell.rowId, row)
    }

    const selectedPositions = new Set(virtualSelectedCells.map(cell => `${cell.r}:${cell.c}`))
    for (const cell of virtualSelectedCells) {
        rows
            .get(String(cell.rowId))
            ?.set(
                String(cell.colId),
                `shadow-borders${!selectedPositions.has(`${cell.r - 1}:${cell.c}`) ? ' shadow-border-t' : ''}${!selectedPositions.has(`${cell.r}:${cell.c + 1}`) ? ' shadow-border-r' : ''}${!selectedPositions.has(`${cell.r + 1}:${cell.c}`) ? ' shadow-border-b' : ''}${!selectedPositions.has(`${cell.r}:${cell.c - 1}`) ? ' shadow-border-l' : ''}`
            )
    }

    return rows
}

// Preserve selections in unaffected rows so compiled row rendering can reuse its cell list.
export function useGridRowSelection(selectedCells: Cell[], virtualSelectedCells: VirtualCell[]) {
    const nextRows = buildRowSelection(selectedCells, virtualSelectedCells)
    const [rows, setRows] = useState<ReadonlyMap<string, RowSelection>>(nextRows)

    const sharedRows = new Map<string, RowSelection>()
    let changed = rows.size !== nextRows.size

    for (const [rowId, nextSelection] of nextRows) {
        const previous = rows.get(rowId)
        const unchanged =
            previous !== undefined &&
            previous.size === nextSelection.size &&
            Array.from(nextSelection).every(([colId, borders]) => previous.get(colId) === borders)
        sharedRows.set(rowId, unchanged ? previous : nextSelection)
        if (!unchanged) changed = true
    }

    if (changed) setRows(sharedRows)
    return rows
}
